import { Fragment, useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { BasicFade } from '@components/animators/fade';
import { Card } from '@components/card';
import { useFormField } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormStoreInstance } from '@components/form/create-form-store';
import { IconTeam } from '@components/icons/team';
import { IconTrigger } from '@components/icons/trigger';
import { SelectMulti, SelectOption } from '@components/selectmulti';
import { Stack } from '@components/stack';
import { StateGroup } from '@components/state-selector/state-group';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { VisuallyHidden } from '@components/visually-hidden';
import { useScopedTranslate } from '@locale/translate';

import { useActiveWorkspaceUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';

type ExecutionMethodProps = {
  isAutomatic?: boolean;
  assignees?: User[];
  formStore?: FormStoreInstance;
  hideLabel?: boolean;
};

export const ExecutionMethod = ({
  isAutomatic = true,
  assignees = [],
  hideLabel = false,
  formStore,
}: ExecutionMethodProps) => {
  const { t } = useScopedTranslate(locale);
  const { shortId: workspaceId } = useCurrentWorkspace();
  const usersQuery = useActiveWorkspaceUsers(isAutomatic ? '' : workspaceId);

  const defaultExecutionMethod = useRef(isAutomatic ? 'automatic' : 'manual').current;

  const assigneeList =
    assignees.map((user) => ({
      value: user.shortId,
      label: user.name,
    })) ?? [];

  const assignUsersProps = useFormField<SelectOption[]>({
    id: 'assignees',
    label: t('assignTo'),
    defaultValue: assigneeList,
  });

  const executionMethodProps = useFormField<string>({
    id: 'executionMethod',
    label: t('execution'),
    defaultValue: defaultExecutionMethod,
  });

  const assigneesSwitchProps = useFormField<boolean>({
    id: 'assigneesSwitch',
    label: t('assign'),
    defaultValue: assignees.length > 0,
  });

  const FormLabelContainer = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={4}>
      <Stack>
        <StateGroup
          label={
            <FormLabelContainer>
              <FormLabel text={executionMethodProps.label} info={t('methodInfo')} />
            </FormLabelContainer>
          }
          orientation="horizontal"
          selectedStateId={executionMethodProps.value || ''}
          onStateSelected={(state) => {
            executionMethodProps.onChange(state.id);
            if (formStore) {
              // Need to re-validate the form, since validation on some fields can change
              // but needs to happen after the next render, hence the setTimeout... which I hate
              setTimeout(formStore.actions.validateForm);
            }
          }}
          stateItems={[
            {
              id: 'automatic',
              orientation: 'horizontal',
              icon: <IconTrigger />,
              label: t('automatic'),
            },
            {
              id: 'manual',
              orientation: 'horizontal',
              icon: <IconTeam />,
              label: t('manual'),
            },
          ]}
        />
      </Stack>
      <AnimatePresence initial={false} exitBeforeEnter>
        {executionMethodProps.value === 'automatic' ? (
          <BasicFade key="automatic" exit={undefined}>
            <Text font="regular14" text={t('automaticHelpText')} />
          </BasicFade>
        ) : (
          <BasicFade key="manual" exit={undefined}>
            <Stack space={4}>
              <Text font="regular14" text={t('manualHelpText')} />
              <Card space={4}>
                <Stack space={3}>
                  <Switch
                    {...assigneesSwitchProps}
                    onChange={(value) => {
                      if (!value) {
                        assignUsersProps.onChange([]);
                      }
                      assigneesSwitchProps.onChange(value);
                    }}
                  />
                  <AnimatePresence initial={false}>
                    {assigneesSwitchProps.value && (
                      <motion.div
                        transition={{ duration: 0.2 }}
                        animate={{ opacity: 1 }}
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <SelectMulti
                          {...assignUsersProps}
                          options={
                            usersQuery.data?.map((user) => ({
                              value: user.shortId,
                              label: user.name,
                            })) ?? []
                          }
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Stack>
              </Card>
            </Stack>
          </BasicFade>
        )}
      </AnimatePresence>
    </Stack>
  );
};

const locale = {
  en: {
    assignTo: 'Assign to',
    assign: 'Assign to user',
    description: 'Description',
    execution: 'Trigger method',
    methodInfo: 'The method of which the action is started by',
    // automatic
    automaticHelpText: 'Is sent automatically when previous step is completed',
    automatic: 'Automatic',
    // manual
    manual: 'Manual',
    manualHelpText: '• Needs to be manually sent\n• Can be assigned to specific user (optional) ',
  },
};
